/**
 * AtlasShaderTourModule — the SHADER TOUR (2026-09-18).
 *
 * "Add in a tour for all the different shaders … you go through each shader at
 * a time, you move around for 30 seconds, collect all the metrics about the
 * shader and do a screenshot at the end to be saved in a debug folder in atlas
 * so you can review the photos per shader … also all shaders at once and
 * combinations of shaders that would interfere with each other."
 *
 * THE TOUR (`lib/shader_tour.ts` plans it, this module walks it). From where
 * the admin stands: remember the spot and the look; pin auto-tune, the FSR
 * governor and the time of day; read the catalogued shaders' current values
 * off the preset. Then per leg — the BASELINE (every catalogued shader off),
 * one SOLO per shader (with its requirements), ALL at once, and every
 * INTERFERENCE COMBO — set the shaders through the graphics menu's batched
 * raw setters (`__gfx.setMany`; the post chain rebuilds once), wait for the
 * chain to settle and the frame to be LIT (the settledGrab law), walk
 * `legSeconds` with the yaw sweeping (the input module's tour drive, never a
 * synthetic key) while the frame clock samples, then RETURN to the spot and
 * the look (`debugTeleport` + the yaw/pitch), settle again, and take the
 * picture through the engine's readback (`__frameCapture` — the only capture
 * that works on WebGPU, whose canvas keeps no drawing buffer). Every leg's
 * PNG + JSON go to `atlas/debug/shader-tour/<run>/` through the dev server's
 * debug sink (`POST /__atlas/debug-save`, vite.config.ts); with no sink the
 * files download instead. `run.json` + `summary.md` close the run; the flags
 * are restored to what the player had.
 *
 * THE METRICS per leg: frames, fps, frame-ms p50/p95/p99/max/mean, per-frame
 * draw calls and triangles, the pipeline count before/after (the pipelines a
 * shader ADDS), heap, GPU ms (timestamp-query backends), Long Animation
 * Frames, GPU errors and console errors/warnings captured during the leg
 * (the renderer's first-of-its-kind device errors reach console.error), the
 * pass list, and the picture's luminance stats (a BLACK FRAME is flagged).
 *
 * Levers: the Probes window's "Shader tour" card, or
 * `__shaderTour.run({ legSeconds, only, groups, include, combos, all, shotWidth,
 * label })` → the run summary; `.status()`, `.abort()`, `.plan(opts)`.
 */
import type { App, IModule } from 'enginex/core/App.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { atlas } from '../state.js';
import { activeTier, activePreset, setAutoTune } from '../lib/quality_tiers.js';
import { currentTod } from './SkyModule.js';
import {
  SHADER_CATALOG, planTour, legEntries, readOriginals, legFileBase, summariseTour, summaryMarkdown, shaderById,
  type PlanOptions, type TourLeg, type LegMetrics, type LegResult, type ShaderApply, type ShaderGroup,
} from '../lib/shader_tour.js';
import type { AtlasInputModule } from './AtlasInputModule.js';
import type { AtlasProbesModule } from './AtlasProbesModule.js';

export interface TourOptions extends PlanOptions {
  legSeconds?: number;      // the walk (30)
  settleMs?: number;        // after a flag change (4000)
  shotWidth?: number;       // the picture's width in px (1280; the height follows the aspect)
  turnRadPerS?: number;     // the yaw sweep (0.15)
  pinTod?: boolean;         // hold the time of day where it is (true)
  label?: string;
}
export interface TourStatus {
  running: boolean; runKey: string; folder: string; phase: string; legIndex: number; legCount: number; leg: string;
  lastLeg: { id: string; fps: number; msP95: number; file: string | null } | null; done: boolean; error: string; startedAt: number; sink: string;
}
interface Reducers { debugTeleport(a: { posX: number; posZ: number }): Promise<void> }
interface Gfx { preset(): Record<string, unknown>; passes(): string[]; setMany(entries: Array<[string, unknown]>): number }

const g = globalThis as unknown as Record<string, unknown>;
const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));
const pct = (sorted: number[], q: number): number => (sorted.length ? sorted[Math.min(sorted.length - 1, Math.max(0, Math.floor(q * (sorted.length - 1))))] : 0);
const mean = (a: number[]): number => (a.length ? a.reduce((s, v) => s + v, 0) / a.length : 0);
const SINK_URL = '/__atlas/debug-save';

export class AtlasShaderTourModule implements IModule {
  readonly name = 'ShaderTour';
  private app: App | null = null;
  private status: TourStatus = this.fresh();
  private abortRequested = false;
  private sampling = false;
  private lastFrameAt = 0;
  private frameTimes: number[] = [];
  private draws: number[] = [];
  private tris: number[] = [];
  private statusEl: HTMLElement | null = null;
  private folderEl: HTMLElement | null = null;
  private consoleErrors: string[] = [];
  private consoleWarnings = 0;
  private hooked = false;

  init(app: App): void {
    this.app = app;
    app.world.addSystem(makeSystem('AtlasShaderTourClock', Phase.UI, 92, () => this.frame()));
    (window as unknown as Record<string, unknown>).__shaderTour = Object.assign((opts?: TourOptions) => this.run(opts ?? {}), {
      run: (opts?: TourOptions) => this.run(opts ?? {}),
      status: () => this.status,
      abort: () => this.abort(),
      plan: (opts?: PlanOptions) => planTour(opts ?? {}),
      catalog: () => SHADER_CATALOG.map((d) => ({ id: d.id, label: d.label, group: d.group, apply: d.apply, setter: d.setter })),
    });
  }

  dispose(): void { this.abort(); delete (window as unknown as Record<string, unknown>).__shaderTour; }
  abort(): void { if (this.status.running) { this.abortRequested = true; this.status.phase = 'aborting'; } }

  private fresh(): TourStatus {
    return { running: false, runKey: '', folder: '', phase: 'idle', legIndex: 0, legCount: 0, leg: '', lastLeg: null, done: false, error: '', startedAt: 0, sink: '' };
  }

  // ── the frame clock ──
  private frame(): void {
    const now = performance.now();
    if (this.sampling && this.lastFrameAt > 0) {
      this.frameTimes.push(now - this.lastFrameAt);
      const native = this.app?.renderer.native as unknown as { info?: { render?: { drawCalls?: number; triangles?: number } } } | undefined;
      this.draws.push(native?.info?.render?.drawCalls ?? 0);
      this.tris.push(native?.info?.render?.triangles ?? 0);
    }
    this.lastFrameAt = now;
    if (this.statusEl && (this.frameTimes.length & 7) === 0) this.statusEl.textContent = this.statusLine();
  }

  private statusLine(): string {
    const s = this.status;
    if (!s.running) return s.done ? `done: ${s.legCount} legs → ${s.folder} (${s.sink})${s.error ? ` — ${s.error}` : ''}` : (s.error || 'idle');
    return `${s.phase} · leg ${s.legIndex + 1}/${s.legCount} · ${s.leg}` + (s.lastLeg ? ` · last: ${s.lastLeg.fps.toFixed(1)} fps, p95 ${s.lastLeg.msP95.toFixed(0)} ms` : '');
  }

  /** The Probes window renders this card (AtlasProbesModule). */
  buildSection(body: HTMLElement): void {
    const note = document.createElement('div');
    note.style.cssText = 'font-size:11px;color:#9cf;margin-bottom:6px';
    note.textContent = 'From where you stand: every shader alone, all at once, and the interference combinations — a walk per leg with the metrics sampled, then a picture from this spot. PNG + JSON per leg land in atlas/debug/shader-tour/<run>/ (summary.md has the table). Admins, Commons only.';
    body.appendChild(note);
    const groupsRow = document.createElement('div');
    groupsRow.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;margin-bottom:4px;font-size:11px;color:#aab';
    const gBoxes: Array<{ group: ShaderGroup; box: HTMLInputElement }> = [];
    for (const grp of ['post', 'lighting', 'material', 'water', 'sky'] as ShaderGroup[]) {
      const l = document.createElement('label'); const b = document.createElement('input'); b.type = 'checkbox'; b.checked = true; b.dataset.shaderGroup = grp;
      l.append(b, ` ${grp}`); groupsRow.appendChild(l); gBoxes.push({ group: grp, box: b });
    }
    body.appendChild(groupsRow);
    const optRow = document.createElement('div');
    optRow.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;margin-bottom:4px;font-size:11px;color:#aab;align-items:center';
    const mk = (label: string, key: string, checked = true): HTMLInputElement => { const l = document.createElement('label'); const b = document.createElement('input'); b.type = 'checkbox'; b.checked = checked; b.dataset.shaderTour = key; l.append(b, ` ${label}`); optRow.appendChild(l); return b; };
    const allBox = mk('all at once', 'all'), comboBox = mk('combinations', 'combos'), nextBox = mk('next-entry flags too', 'next-entry'), todBox = mk('pin time of day', 'tod');
    body.appendChild(optRow);
    const ctl = document.createElement('div');
    ctl.style.cssText = 'display:flex;gap:6px;align-items:center;margin-bottom:6px;font-size:11px;color:#aab;flex-wrap:wrap';
    const secs = document.createElement('input'); secs.type = 'number'; secs.value = '30'; secs.min = '3'; secs.max = '300'; secs.dataset.shaderTour = 'seconds';
    const width = document.createElement('input'); width.type = 'number'; width.value = '1280'; width.min = '320'; width.max = '3840'; width.step = '160'; width.dataset.shaderTour = 'width';
    for (const i of [secs, width]) i.style.cssText = 'width:64px;background:#141821;color:#dde;border:1px solid #3a4a66;border-radius:4px;padding:1px 4px';
    const run = document.createElement('button'); run.textContent = 'Run shader tour'; run.dataset.shaderTour = 'run';
    const stop = document.createElement('button'); stop.textContent = 'Abort'; stop.dataset.shaderTour = 'abort';
    for (const b of [run, stop]) b.style.cssText = 'background:#243046;color:#dde;border:1px solid #3a4a66;border-radius:4px;padding:2px 8px;cursor:pointer';
    ctl.append('Seconds per leg', secs, 'shot width', width, run, stop);
    body.appendChild(ctl);
    const st = document.createElement('div'); st.style.cssText = 'font-size:11px;color:#dde;min-height:14px'; st.textContent = this.statusLine(); st.dataset.shaderTour = 'status';
    body.appendChild(st); this.statusEl = st;
    const folder = document.createElement('div'); folder.style.cssText = 'font-size:11px;color:#8fa3bd;min-height:14px'; folder.dataset.shaderTour = 'folder';
    body.appendChild(folder); this.folderEl = folder;
    run.onclick = (): void => {
      const groups = gBoxes.filter((b) => b.box.checked).map((b) => b.group);
      const include: ShaderApply[] = nextBox.checked ? ['rebuild', 'live', 'next-entry'] : ['rebuild', 'live'];
      void this.run({ groups, include, all: allBox.checked, combos: comboBox.checked, pinTod: todBox.checked, legSeconds: Number(secs.value) || 30, shotWidth: Number(width.value) || 1280, label: 'probes-window' }).catch(() => { /* reported in the status */ });
    };
    stop.onclick = (): void => this.abort();
  }

  // ── the run ──
  async run(opts: TourOptions): Promise<{ runKey: string; folder: string; sink: string; legs: number; rows: ReturnType<typeof summariseTour>; error: string }> {
    if (this.status.running) throw new Error('shader tour already running');
    const app = this.app; const conn = atlas.conn as unknown as { reducers: Reducers } | null;
    if (!app || !conn) throw new Error('not connected');
    if (atlas.currentWorldId === null || atlas.currentWorldId !== atlas.commonsWorldId) throw new Error('the shader tour walks the Commons — Tab there first');
    const gfx = g.__gfx as Gfx | undefined;
    const input = app.getModule<AtlasInputModule>('AtlasInput');
    if (!gfx?.setMany || !input) throw new Error('shader tour: the graphics lever or the input module is missing');
    const include: ShaderApply[] = opts.include ?? ['rebuild', 'live', 'next-entry'];
    const legs = planTour({ ...opts, include });
    if (!legs.length) throw new Error('nothing to tour');
    const legSeconds = Math.max(2, Math.min(600, opts.legSeconds ?? 30));
    const settleMs = opts.settleMs ?? 4000;
    const shotWidth = Math.max(160, Math.min(3840, opts.shotWidth ?? 1280));
    const turn = opts.turnRadPerS ?? 0.15;
    const runKey = `st-${Date.now().toString(36)}-${Math.floor(Math.random() * 46656).toString(36)}`;
    const folder = `shader-tour/${runKey}`;
    const backend = (app.renderer.native as unknown as { backend?: { isWebGPUBackend?: boolean } }).backend?.isWebGPUBackend ? 'webgpu' : 'webgl2';
    const origin = { x: atlas.localX, z: atlas.localZ, yaw: atlas.localYaw, pitch: atlas.localPitch };
    const originals = readOriginals(gfx.preset());
    const s: TourStatus = { ...this.fresh(), running: true, runKey, folder: `atlas/debug/${folder}`, legCount: legs.length, startedAt: Date.now(), phase: 'starting' };
    this.status = s; this.abortRequested = false;
    if (this.folderEl) this.folderEl.textContent = `run ${runKey} → ${s.folder}`;
    // THE PINNED-TIER LAW (the perf tour's lesson): auto-tune and the governor would move the preset under the tour.
    const originalAutoTune = activePreset().autoTune ?? false;
    let originalAdaptive: string | null = null;
    try { originalAdaptive = localStorage.getItem('atlas:adaptive'); localStorage.setItem('atlas:adaptive', 'off'); } catch { /* no storage */ }
    if (originalAutoTune) setAutoTune(false);
    const setTod = g.__setTod as ((v: number | null) => void) | undefined;
    if (opts.pinTod !== false && setTod) { try { setTod(currentTod()); } catch { /* the sky is not up */ } }
    this.hookConsole();
    const results: LegResult[] = [];
    let sink = 'server';
    try {
      for (let li = 0; li < legs.length; li++) {
        const leg = legs[li]; s.legIndex = li; s.leg = leg.label;
        if (this.abortRequested) throw new Error('aborted');
        const startedAt = Date.now(); const t0 = performance.now();
        let error: string | undefined;
        let metrics = this.emptyMetrics(); let file: string | null = null; let legSink = sink;
        try {
          s.phase = 'setting shaders';
          gfx.setMany(legEntries(leg, include));
          await sleep(settleMs); await this.waitLit(20_000);
          const pipelinesBefore = this.pipelines();
          this.consoleErrors = []; this.consoleWarnings = 0;
          // the walk
          s.phase = 'walking';
          this.frameTimes = []; this.draws = []; this.tris = []; this.lastFrameAt = 0; this.sampling = true;
          input.setAutoWalk(1, 0, turn);
          const walkStart = performance.now();
          try { await sleep(legSeconds * 1000); } finally { input.setAutoWalk(0, 0, 0); this.sampling = false; }
          const elapsedS = Math.max(0.25, (performance.now() - walkStart) / 1000);
          const sorted = [...this.frameTimes].sort((a, b) => a - b);
          const loafs = ((g.__loaf as ((n: number) => Array<{ t: number; dur: number }>) | undefined)?.(600) ?? []).filter((l) => l.t >= walkStart);
          const heap = (performance as unknown as { memory?: { usedJSHeapSize?: number } }).memory?.usedJSHeapSize;
          const native = app.renderer.native as unknown as { info?: { render?: { timestamp?: number } } };
          metrics = {
            frames: sorted.length, fps: sorted.length / elapsedS,
            frameMs: { p50: pct(sorted, 0.5), p95: pct(sorted, 0.95), p99: pct(sorted, 0.99), max: sorted.length ? sorted[sorted.length - 1] : 0, mean: mean(sorted) },
            drawCalls: mean(this.draws), triangles: mean(this.tris),
            pipelinesBefore, pipelinesAfter: this.pipelines(),
            heapMb: heap ? heap / 1048576 : 0, gpuMs: Number(native.info?.render?.timestamp ?? 0) || 0,
            loafs: loafs.length, loafMs: loafs.reduce((a, l) => a + (l.dur ?? 0), 0),
            gpuErrors: this.consoleErrors.filter((m) => /gpu|wgsl|pipeline|shader|webgl|device/i.test(m)).slice(0, 12),
            consoleErrors: this.consoleErrors.length, consoleWarnings: this.consoleWarnings,
            passes: gfx.passes(), picture: null,
          };
          // back to the vantage for the picture
          s.phase = 'returning';
          await conn.reducers.debugTeleport({ posX: origin.x, posZ: origin.z });
          await this.waitFor(() => Math.hypot(atlas.localX - origin.x, atlas.localZ - origin.z) < 3, 25_000);
          atlas.localYaw = origin.yaw; atlas.localPitch = origin.pitch;
          await sleep(Math.min(settleMs, 2500)); await this.waitLit(15_000);
          s.phase = 'picture';
          const shot = await this.capture(shotWidth);
          if (shot) {
            metrics.picture = { mean: shot.mean, dark: shot.dark, bright: shot.bright, blackFrame: shot.mean < 2 || shot.dark > 0.995, w: shot.w, h: shot.h };
            const base = legFileBase(li, leg.id);
            const saved = await this.save(`${folder}/${base}.png`, shot.png, 'image/png');
            legSink = saved.sink; file = saved.file; if (saved.sink !== 'server') sink = saved.sink;
          }
        } catch (e) {
          error = String((e as Error)?.message ?? e);
          if (error === 'aborted') throw e;
        }
        const nextEntry = leg.on.filter((id) => shaderById(id)?.apply === 'next-entry');
        const res: LegResult = { leg, metrics, file, sink: legSink, nextEntry, startedAt, ms: performance.now() - t0, error };
        results.push(res);
        await this.save(`${folder}/${legFileBase(li, leg.id)}.json`, JSON.stringify({ runKey, leg, tier: activeTier(), backend, metrics, file, nextEntry, error }, null, 2), 'application/json');
        s.lastLeg = { id: leg.id, fps: metrics.fps, msP95: metrics.frameMs.p95, file };
      }
      s.phase = 'summary';
    } catch (e) {
      s.error = String((e as Error)?.message ?? e);
    } finally {
      input.setAutoWalk(0, 0, 0); this.sampling = false;
      s.phase = 'restoring';
      try { gfx.setMany(originals); } catch { /* the lever is gone */ }
      if (setTod) { try { setTod(null); } catch { /* ignore */ } }
      if (originalAutoTune) setAutoTune(true);
      try { if (originalAdaptive === null) localStorage.removeItem('atlas:adaptive'); else localStorage.setItem('atlas:adaptive', originalAdaptive); } catch { /* no storage */ }
      this.unhookConsole();
    }
    const rows = summariseTour(results);
    const run = { runKey, label: opts.label ?? 'lever', startedAt: s.startedAt, tier: activeTier(), backend, legSeconds, where: { x: origin.x, z: origin.z }, folder: s.folder, userAgent: navigator.userAgent, include, error: s.error };
    await this.save(`${folder}/run.json`, JSON.stringify({ ...run, rows, legs: results.map((r) => ({ ...r, leg: r.leg })) }, null, 2), 'application/json');
    await this.save(`${folder}/summary.md`, summaryMarkdown(run, rows), 'text/markdown');
    s.running = false; s.done = true; s.sink = sink; s.phase = s.error ? 'failed' : 'done';
    if (this.statusEl) this.statusEl.textContent = this.statusLine();
    app.getModule<AtlasProbesModule>('Probes')?.addRecord('shader-tour', { runKey, folder: s.folder, sink, legs: results.length, error: s.error, rows }, !s.error, Date.now() - s.startedAt);
    return { runKey, folder: s.folder, sink, legs: results.length, rows, error: s.error };
  }

  private emptyMetrics(): LegMetrics {
    return { frames: 0, fps: 0, frameMs: { p50: 0, p95: 0, p99: 0, max: 0, mean: 0 }, drawCalls: 0, triangles: 0, pipelinesBefore: 0, pipelinesAfter: 0, heapMb: 0, gpuMs: 0, loafs: 0, loafMs: 0, gpuErrors: [], consoleErrors: 0, consoleWarnings: 0, passes: [], picture: null };
  }
  private pipelines(): number { const p = (g.__pipelines as (() => { pipelines?: number | null }) | undefined)?.(); return Math.round(p?.pipelines ?? 0); }

  /** A picture is taken only once the frame is LIT — a rebuilt post chain draws nothing until its async build lands. */
  private async waitLit(maxMs: number): Promise<void> {
    const fp = g.__framePixels as ((stride?: number) => Promise<{ mean?: number; paused?: boolean }>) | undefined;
    if (!fp) { await sleep(500); return; }
    const t0 = performance.now();
    while (performance.now() - t0 < maxMs) {
      try { const r = await fp(16); if ((r.mean ?? 0) > 3) return; } catch { /* readback cut by a rebuild */ }
      await sleep(400);
    }
  }
  private async waitFor(cond: () => boolean, maxMs: number): Promise<boolean> {
    const t0 = performance.now();
    while (performance.now() - t0 < maxMs) { if (cond()) return true; await sleep(250); }
    return cond();
  }
  private async capture(maxWidth: number): Promise<{ w: number; h: number; mean: number; dark: number; bright: number; png: string } | null> {
    const fc = g.__frameCapture as ((maxWidth: number) => Promise<{ w: number; h: number; mean: number; dark: number; bright: number; png: string; paused?: boolean }>) | undefined;
    if (!fc) return null;
    for (let i = 0; i < 3; i++) { try { const r = await fc(maxWidth); if (!r.paused && r.png) return r; } catch { /* retry */ } await sleep(300); }
    return null;
  }

  /** The debug sink (dev server) or a download; base64 for binaries, text for the rest. */
  private async save(path: string, data: string, mime: string): Promise<{ sink: string; file: string | null }> {
    const isText = !mime.startsWith('image/');
    try {
      const res = await fetch(SINK_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(isText ? { path, text: data } : { path, base64: data }) });
      if (res.ok) { const j = (await res.json()) as { ok?: boolean; file?: string }; if (j.ok) return { sink: 'server', file: j.file ?? path }; }
    } catch { /* no dev sink (a static host, or the user's Vite started before the plugin existed) */ }
    try {
      const blob = isText ? new Blob([data], { type: mime }) : new Blob([Uint8Array.from(atob(data), (c) => c.charCodeAt(0))], { type: mime });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = path.replace(/\//g, '__'); a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
      return { sink: 'download', file: a.download };
    } catch { return { sink: 'none', file: null }; }
  }

  private origError: ((...a: unknown[]) => void) | null = null;
  private origWarn: ((...a: unknown[]) => void) | null = null;
  private hookConsole(): void {
    if (this.hooked) return;
    this.hooked = true;
    this.origError = console.error; this.origWarn = console.warn;
    const self = this;
    console.error = function (...a: unknown[]): void { self.consoleErrors.push(a.map((x) => (typeof x === 'string' ? x : String((x as Error)?.message ?? x))).join(' ').slice(0, 300)); self.origError?.apply(console, a); };
    console.warn = function (...a: unknown[]): void { self.consoleWarnings++; self.origWarn?.apply(console, a); };
  }
  private unhookConsole(): void {
    if (!this.hooked) return;
    if (this.origError) console.error = this.origError;
    if (this.origWarn) console.warn = this.origWarn;
    this.hooked = false;
  }
}
